/**
 * Shared pieces of the Listeners statistics views: the date-range presets, the
 * duration formatter, and the API response types.
 *
 * Every figure is computed by the API (AdminListenerStatsService) — the panel
 * only formats. Login time, online time, call time and earnings are separate
 * metrics from separate sources; nothing here derives one from another.
 */

export type RangePreset =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

export const RANGE_PRESETS: Array<{ value: RangePreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
];

export interface StatsRangeParams {
  /** ISO, inclusive. */
  from: string;
  /** ISO, exclusive. */
  to: string;
  /** Browser's IANA zone — the server buckets days / "today" in it. */
  tz: string;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/**
 * Local-calendar range for a preset. Ends are exclusive midnights, so "Today"
 * is [00:00 today, 00:00 tomorrow). `custom` takes `YYYY-MM-DD` strings from
 * the range picker (both days included). Returns null for an incomplete custom
 * range.
 */
export function rangeFor(
  preset: RangePreset,
  now: Date,
  custom?: { from: string; to: string },
  tz: string = browserTimeZone(),
): StatsRangeParams | null {
  const today = startOfDay(now);
  let from: Date;
  let to: Date;
  switch (preset) {
    case 'today':
      from = today;
      to = addDays(today, 1);
      break;
    case 'yesterday':
      from = addDays(today, -1);
      to = today;
      break;
    case 'last7':
      from = addDays(today, -6);
      to = addDays(today, 1);
      break;
    case 'last30':
      from = addDays(today, -29);
      to = addDays(today, 1);
      break;
    case 'thisMonth':
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      break;
    case 'lastMonth':
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      to = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'custom': {
      const f = parseDay(custom?.from);
      const t = parseDay(custom?.to);
      if (!f || !t || t < f) return null;
      from = f;
      to = addDays(t, 1);
      break;
    }
  }
  return { from: from.toISOString(), to: to.toISOString(), tz };
}

function parseDay(s?: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '');
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
}

/** Seconds → "7h 05m" / "12m 30s" / "45s". Null → "—". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

/** Seconds elapsed from an ISO instant to `now` (live session timers). */
export function elapsedSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 1000)) : null;
}

/** ISO → "25 Sep, 9:05 pm" in the viewer's locale. Null → "—". */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

// ---- API shapes -------------------------------------------------------------

export interface ListenerRowStats {
  /** null when the admin lacks `listeners.activity.view`. */
  login: null | {
    /** null = no login recorded since tracking began — unknown, not logged out. */
    loggedIn: boolean | null;
    currentLoginSince: string | null;
    lastLoginAt: string | null;
    lastLogoutAt: string | null;
    loginSeconds: number;
  };
  online: null | {
    onlineNow: boolean;
    /** App open with a live heartbeat. Absent on older API builds. */
    screenOn?: boolean;
    /** Phone locked but still callable: when the reach window ends. */
    reachableUntil?: string | null;
    currentOnlineSince: string | null;
    lastWentOnlineAt: string | null;
    lastOnlineAt: string | null;
    onlineSeconds: number;
  };
  calls: { calls: number; connectedCalls: number; callSeconds: number };
  /** null when the admin lacks `listeners.earnings.view`. */
  money: null | { callerCoins: number; earningsPaise: string; callEarningsPaise: string };
}

export interface ListenerSummary {
  totalListeners: number;
  callableListeners: number;
  onlineNow: number;
  offlineNow: number;
  loginSeconds: number | null;
  onlineSeconds: number | null;
  calls: number;
  connectedCalls: number;
  callSeconds: number;
  callerCoins: number | null;
  earningsPaise: string | null;
  callEarningsPaise: string | null;
}

export interface ListenerOverview {
  listener: {
    id: string;
    displayName: string;
    handle: string;
    status: string;
    isSuspended: boolean;
    /** Admin-flagged placeholder listener (absent on older API builds). */
    isDummy?: boolean;
    /** The listener's Home availability switches (absent on older API builds). */
    availability?: { audio: boolean; video: boolean; chat: boolean };
  };
  range: { from: string; to: string; tz: string; now: string };
  activity: null | {
    login: NonNullable<ListenerRowStats['login']> & { loginCount: number; todayLoginSeconds: number };
    online: NonNullable<ListenerRowStats['online']> & {
      lastWentOfflineAt: string | null;
      todayOnlineSeconds: number;
      /** Heartbeat-only (app open) part of today's online time — the basis of
       *  the online-earning stages. Absent on older API builds. */
      todayScreenOnSeconds?: number;
      weekOnlineSeconds: number;
      monthOnlineSeconds: number;
      lifetimeOnlineSeconds: number;
    };
  };
  calls: ListenerRowStats['calls'] & { missedCalls: number; failedCalls: number };
  money: null | (NonNullable<ListenerRowStats['money']> & {
    /** Per gift type in the range (absent on older API builds). Sums to
     *  `giftEarningsPaise`: both are the same ledger credits. */
    giftBreakdown?: GiftEarningLine[];
    giftEarningsPaise: string;
    referralEarningsPaise: string;
    /** Manual admin credits in the range (absent on older API builds). */
    adminCreditEarningsPaise?: string;
    lifetimeEarningsPaise: string;
    availableBalancePaise: string;
    pendingPayoutPaise: string;
    paidOutGrossPaise: string;
    paidOutNetPaise: string;
  });
  /** null when the range is longer than 92 days. */
  series: null | Array<{
    day: string;
    onlineSeconds: number | null;
    /** App-open part of that day's online time (absent on older API builds). */
    screenOnSeconds?: number | null;
    loginSeconds: number | null;
    connectedCalls: number;
    callSeconds: number;
    earningsPaise: string | null;
  }>;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** `GET /admin/listeners/:id/live-streams` — one live stream the listener hosted. */
export interface LiveStreamRow {
  id: string;
  title: string;
  category: string | null;
  status: 'live' | 'paused' | 'ended' | 'ended_flagged' | 'scheduled';
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
  peakViewerCount: number;
  totalViewers: number;
  endedReason: string | null;
}

export interface LoginSessionRow {
  id: string;
  method: string;
  startedAt: string;
  endedAt: string | null;
  lastActiveAt: string;
  durationSeconds: number;
  status: 'active' | 'ended' | 'expired';
  endReason: string | null;
}

export interface OnlineSessionRow {
  id: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
  status: 'active' | 'ended' | 'closing';
  endReason: string | null;
}

/** One gift type's earnings — name/emoji from the gift catalog. */
export interface GiftEarningLine {
  giftCode: string | null;
  giftName: string;
  emoji: string | null;
  quantity: number;
  amountPaise: string;
}

/** "🌹 Rose × 2" — how a gift line is named everywhere in the panel. */
export function giftLabel(g: { name: string; emoji: string | null; quantity: number }): string {
  return `${g.emoji ? `${g.emoji} ` : ''}${g.name} × ${g.quantity}`;
}

export interface EarningRow {
  reason: string;
  referenceType: string;
  referenceId: string;
  firstEntryId: string;
  ledgerEntries: number;
  creditedAt: string;
  earningsPaise: string;
  status: string;
  call: null | {
    mode: 'audio' | 'video';
    callType: 'direct' | 'random';
    status: string;
    callerHandle: string | null;
    /** The caller's own Display Name — absent on older APIs. */
    callerDisplayName?: string | null;
    durationSeconds: number;
    callerCoins: number;
    /** Caller was an admin-flagged test account at call start: 0 coins / ₹0. */
    isVerificationCall?: boolean;
    /** The ring went to a dummy (placeholder) listener: timed attempt, never answered. */
    isDummyAttempt?: boolean;
  };
  /** Set on gift earnings: which gift (a call with several gift types has one
   *  row per type). Absent on older API builds. */
  gift?: null | { code: string | null; name: string; emoji: string | null; quantity: number };
  /** Set on an admin earnings credit (`reason: 'admin_credit'`): the admin's own
   *  reason and who created it. */
  adminCredit?: null | {
    reason: string;
    adminId: string | null;
    adminName: string | null;
    adminEmail: string | null;
    /** Taken back so far / still reversible. Absent on older API builds. */
    reversedPaise?: string;
    remainingPaise?: string;
  };
  /** Set on an admin reversal (`reason: 'admin_credit_reversal'`, a negative
   *  `earningsPaise`): why, who, and which admin credit it took back from. */
  adminReversal?: null | {
    reason: string;
    adminId: string | null;
    adminName: string | null;
    adminEmail: string | null;
    originalTransactionId: string | null;
  };
}

export const END_REASON_LABEL: Record<string, string> = {
  logout: 'Logged out',
  logout_all: 'Logged out (all devices)',
  revoked: 'Token revoked',
  force_logout: 'Admin force-logout',
  blocked: 'User blocked',
  deleted: 'Account deleted',
  expired: 'Session expired',
  offline: 'Went offline',
  timeout: 'Connection lost',
  lease_expired: 'Screen-off reach window ended',
  undelivered_rings: 'Unreachable — calls not delivered to phone',
  foreground_connection_lost: 'Connection lost while app was open',
  suspended: 'Suspended',
};

export const LOGIN_METHOD_LABEL: Record<string, string> = {
  otp: 'OTP',
  firebase: 'Firebase OTP',
  truecaller: 'Truecaller',
  restored: 'Existing session',
};
